// react
import React, { useEffect, useState, lazy, Suspense } from 'react';

// redux
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import Lottie from 'react-lottie';
import animationData from '../lotties/doneNode_fr.json';

// utils
import { useTranslation } from 'react-i18next';
import ModalLarge from './Base/ModalLarge';
import nodeAxiosFirebase from '../utils/nodeAxiosFirebase';
import FieldComponent from '../components/@generalComponents/FieldComponent';
import Button from '../stories/general-components/Button';
import { Typography } from '@mui/material';
import { setGeneralStatus, setRefresh } from '../redux/actions-v2/coreAction';
import Loading from '../stories/general-components/Loading';
import SideExtra from '../components/@generalComponents/SideExtra';



const ModalElementCreation = ({
  isOpen,
  modalCloseHandler,
  fromElement,
  structureId,
  targetId,
  targetProfileId,
  elementPath,
  dependencyId,
  dependencyHook,
  roomId,
  roomComplete,
  handleDone,
  defaultFields,
  blockLayoutDetails,
}) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [isAnimationPlaying, setIsAnimationPlaying] = useState(true);

  useEffect(() => {
    return () => {
      setIsAnimationPlaying(false);
    };
  }, []);

  const defaultOptions = {
    loop: false,
    autoplay: true,
    animationData: animationData,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice',
      progressiveLoad: false,
    },
  };

  const businessStructure = useSelector(
    (state) => state.core.businessStructure
  );

  const businessPreference = useSelector((state) => state.core.businessData);
  const businessStructures = businessStructure?.structures;
  const structure = businessStructures.find((s) => s.id === structureId);
  const currentLangCode = i18n.language;

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [disabledPrim, setDisabledPrim] = useState(false);
  const [message, setMessage] = useState(null);
  const [done, setDone] = useState(false);
  const [section, setSection] = useState(null);
  const [formData, setFormData] = useState({});
  const [currentPage, setCurrentPage] = useState(0);
  const [defaultSetted, setDefaultSetted] = useState(true);

  const [isSiteExtraOpen, setIsSiteExtraOpen] = useState(false);
  const [fieldSettings, setFieldSettings] = useState(null);
  const [fields, setFields] = useState([]);

  const [creationTargetId, setCreationTargetId] = useState(targetId || '');
  const [creationDependencyId, setCreationDependencyId] = useState(
    dependencyId || ''
  );
  const [currentHoveredValue, setCurrentHoveredValue] = useState(null);

  const [creationProfileId, setCreationProfileId] = useState(
    targetProfileId || ''
  );
  const [parentElementCollection, setParentElementCollection] = useState('');
  const [parentElementPath, setParentElementPath] = useState(elementPath || '');
  const [parentData, setParentData] = useState(null);
  const [cleanedFields, setCleanedFields] = useState([]);


  const handleSettingsField = (fieldValue) => {
    setIsSiteExtraOpen(true);
    setFieldSettings(fieldValue);
  };

  useEffect(() => {
    if (isOpen && structure?.fields) {
      const initialFormData = structure.fields.reduce((acc, field) => {
        acc[field.value] = field.typeData === 'search' ? null : '';
        return acc;
      }, {});
      setFormData(initialFormData);
    }
  }, [structure?.fields, fields]);


  useEffect(() => {
    if (isOpen && structure?.fields && cleanedFields?.length > 0 && defaultFields?.length > 0) {
      setDefaultSetted(false)
      const initialFormData = cleanedFields.reduce((acc, field) => {
        acc[field.value] = defaultFields.find(
          (defaultField) => defaultField.key === field.value
        )?.value || '';
        return acc;
      }, {});
      setFormData(initialFormData);
      setDefaultSetted(true)
    }
  }, [
    structure?.fields,
    isOpen,
    defaultFields,
    cleanedFields?.length
  ]);



  useEffect(() => {
    if (isOpen && defaultSetted) {

      let cleanedFields = structure?.fields;

      if (creationTargetId && targetId) {
        cleanedFields = cleanedFields.filter(
          (field) => field.value !== 'targetId'
        );
      }

      if (creationProfileId && targetProfileId) {
        cleanedFields = cleanedFields.filter(
          (field) => field.value !== 'targetProfileId'
        );
      }

      if (creationDependencyId && dependencyId) {
        cleanedFields = cleanedFields.filter(
          (field) => field.value !== 'dependencyId'
        );
      }

      // Filter fields based on visibility and access
      cleanedFields = cleanedFields.filter((field) => {
        // Skip visibility check for required system fields
        if (field.value === 'name') {
          return true;
        }

        if (field.type === 'default') {
          if (field.visibility !== 'hidden') {
            return true;
          } else {
            return false;
          }
        }


        if (!field?.hasAccess) {
          return false;
        }

        switch (field.visibility) {
          case 'edition':
            return false;
          case 'hidden':
            return false;
          case 'both':
          default:
            return true;
        }
      });

      // Filter fields based on their conditions
      cleanedFields = cleanedFields.filter((field) => {
        if (field?.conditional && field?.conditional?.length > 0) {
          return field?.conditional.every((condition) => {
            const fieldValue = formData[condition.field];
            switch (condition.operator) {
              case '==':
                return fieldValue === condition.value;
              case '!=':
                return fieldValue !== condition.value;
              case '>':
                return fieldValue > condition.value;
              case '<':
                return fieldValue < condition.value;
              case 'includes':
                if (Array.isArray(fieldValue)) {
                  return fieldValue.includes(condition.value);
                }
                return String(fieldValue).includes(String(condition.value));
              case 'AND':
                return (
                  field?.conditional.every((cond) =>
                    evalCondition(cond, formData[cond.field])
                  ) && true
                );
              case 'OR':
                return (
                  field?.conditional.some((cond) =>
                    evalCondition(cond, formData[cond.field])
                  ) || false
                );
              default:
                return true;
            }
          });
        }
        return true;
      });

      setCleanedFields(cleanedFields);
    }
  }, [
    structureId,
    formData,
    creationTargetId,
    creationProfileId,
    creationDependencyId,
    isOpen,
  ]);

  const pages = [];
  let currentSection = [];

  cleanedFields.forEach((field) => {
    if (field.typeData === 'page') {
      if (currentSection.length > 0) {
        pages.push(currentSection); // Push the accumulated section as a "page"
      }
      currentSection = []; // Start a new section
    }
    currentSection.push(field);
  });

  // Push any remaining fields as the last page
  if (currentSection.length > 0) {
    pages.push(currentSection);
  }

  // Page navigation functions
  const handleNextPage = () => {
    if (currentPage < pages.length - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  // Get fields for the current page
  const fieldsOnCurrentPage = pages[currentPage] || [];

  // Helper function to evaluate conditions
  const evalCondition = (condition, fieldValue) => {
    switch (condition.operator) {
      case '==':
        return fieldValue === condition.value;
      case '!=':
        return fieldValue !== condition.value;
      case '>':
        return fieldValue > condition.value;
      case '<':
        return fieldValue < condition.value;
      case 'includes':
        if (Array.isArray(fieldValue)) {
          return fieldValue.includes(condition.value);
        }
        return String(fieldValue).includes(String(condition.value));
      default:
        return true;
    }
  };

  // Create a function to handle changes to the fields
  const handleFieldChange = (fieldId, value) => {
    let convertedValue = value;
    setFormData((prevState) => ({ ...prevState, [fieldId]: convertedValue }));
  };

  const PATH_MAPPING = {
    services: '/services',
    cardsinvoiced: '/cards',
    cardsexpense: '/cards',
    contacts: '/users',
    cardsuninvoiced: '/cards',
    passes: '/passes',
    payments: '/payments',
    articles: '/articles',
    profiles: '/profiles',
    storages: '/storages',
    tasks: '/tasks',
    grids: '/grids',
    nodies: '/nodies',
    items: '/items'
  };

  const resolvePath = React.useCallback((structure) => {
    if (!structure?.collectionField) {
      console.warn('Structure or collectionField is undefined');
      return '';
    }

    const path = PATH_MAPPING[structure.collectionField];
    if (!path) {
      console.warn(`Unknown collectionField: ${structure.collectionField}`);
      return '';
    }

    return path;
  }, []);

  // Memoize complex calculations
  const finalDep = React.useMemo(() => {
    if (dependencyHook?.startsWith('targetId')) {
      return `users/${targetId}`;
    }
    if (dependencyHook?.startsWith('targetProfileId')) {
      return `profiles/${targetProfileId}`;
    }
    if (dependencyHook?.startsWith('id')) {
      return elementPath;
    }
    if (!formData?.dependencyId) {
      const specialCollections = ['storages', 'tasks', 'nodies', 'passes'];
      if (specialCollections.includes(structure?.collectionField)) {
        if (creationProfileId) return `profiles/${creationProfileId}`;
        if (creationTargetId) return `users/${creationTargetId}`;
        return '';
      }
      return '';
    }
    return formData?.dependencyId || '';
  }, [dependencyHook, targetId, targetProfileId, elementPath, formData?.dependencyId, structure?.collectionField, creationProfileId, creationTargetId]);

  const finalElementPath = React.useMemo(() => finalDep + resolvePath(structure), [finalDep, resolvePath, structure]);

  const finalPath = React.useMemo(() => {
    if (finalElementPath?.startsWith('profiles')) {
      return `users/${targetId}/connections/${targetId}${businessPreference?.id}/${finalElementPath}`;
    }
    return finalElementPath;
  }, [finalElementPath, targetId, businessPreference?.id]);

  useEffect(() => {
    let newErrors = {};

    cleanedFields.forEach((field) => {
      if (
        field.required &&
        (formData[field.value] === undefined ||
          formData[field.value] === null ||
          formData[field.value] === '')
      ) {
        newErrors[field.value] = 'This field is required';
      }
    });
    if (Object.keys(newErrors)?.length > 0) {
      setDisabledPrim(true);
      return;
    } else {
      setDisabledPrim(false);
    }
  }, [formData, cleanedFields]);

  const handleCreate = async () => {
    const formatDatesInObject = (obj) => {
      for (let key in obj) {
        if (obj[key] instanceof Date) {
          obj[key] = moment(obj[key]).format("YYYY-MM-DDTHH:mm:ss.SSSZ");
        }
      }
      return obj;
    };

    const formatedData = {
      name: formData?.name || '-',
      tags: formData?.tags || [],
      elementPath: finalPath,
      structureId: structure?.id,
      blockStructure: blockLayoutDetails?.blockStructure,
      tabIndex: blockLayoutDetails?.tabIndex,
      blockIdentifiant: blockLayoutDetails?.blockIdentifiant,
      lang: currentLangCode?.slice(0, 2) || 'fr',
      data: formatDatesInObject({
        ...formData,
        targetProfileId:
          formData?.targetProfileId ||
          (creationProfileId ? 'profiles/' + creationProfileId : null),
        dependencyId: formData?.dependencyId || finalDep,
        targetId: creationTargetId ? 'users/' + creationTargetId : null,
      }),
    };

    let newErrors = {};
    cleanedFields.forEach((field) => {
      if (
        field.required &&
        (formData[field.value] === undefined || formData[field.value] === null)
      ) {
        newErrors[field.value] = 'This field is required';
      }
    });

    // If there are any errors, don't submit the form
    if (Object.keys(newErrors)?.length > 0) {
      setErrors(newErrors);
      return;
    } else {
      setErrors({});
    }

    try {
      setLoading(true);
      const response = await nodeAxiosFirebase({
        t,
        method: 'POST',
        url: `coreSeqV4`,
        body: formatedData,
      });
      const newElementId = response?.elementId;


      if (roomId) {
        try {
          dispatch(
            setGeneralStatus({
              status: 'loading',
              position: 'modal',
              type: 'pulse',
            })
          );
          await nodeAxiosFirebase({
            t,
            method: 'POST',
            url: `coreSeqV4/node-element`,
            body: {
              name: formData?.name,
              structureId: structure?.id,
              dropId: roomId,
              elementPath: resolvePath(structure) + '/' + newElementId,
              moduleId: '',
              collectionField: structure?.collectionField,
            },
          });
          dispatch(
            setGeneralStatus({
              status: 'success',
              position: 'modal',
              type: 'pulse',
            })
          );
          handleClose();
          roomComplete();
          handleDone(response?.data);
        } catch (error) {
          console.error('Failed to add element to room');
          dispatch(setGeneralStatus({ status: 'error', error: error }));
        }
      } else if (newElementId) {
        if (
          structure?.collectionField === 'cardsinvoiced' ||
          structure?.collectionField === 'cardsuninvoiced' ||
          structure?.collectionField === 'contacts' ||
          structure?.collectionField === 'services' ||
          structure?.collectionField === 'articles'
        ) {
          setLoading(false);
          setDone(true);
          setSection(
            structure?.collectionField === 'cardsinvoiced' ||
              structure?.collectionField === 'cardsuninvoiced'
              ? 'cards'
              : structure?.collectionField === 'contacts'
                ? 'user'
                : 'operations'
          );
          setMessage(newElementId);
          if (fromElement) {
            dispatch(setRefresh({ status: true, structureId: structure?.id }));
          }
          handleDone && handleDone(response?.data);
        } else {
          handleClose();
          if (fromElement) {
            dispatch(setRefresh({ status: true, structureId: structure?.id }));
          }
          handleDone && handleDone(response?.data);
        }
      } else {
        setLoading(false);
        setMessage('An error occurred while creating the element');

      }
    } catch (error) {
      console.error('Failed to create element', error);
      dispatch(setGeneralStatus({ status: 'error', error: error }));
    }
  };

  const handleClose = () => {
    modalCloseHandler();
    setDone(false);
  };

  const handleOpenElement = () => {
    if (section === 'cards') {
      navigate(
        `/bos/element/${structure?.collectionField}/${structure?.id}/${message}`
      );
    } else if (section === 'user') {
      navigate(
        `/bos/element/${structure?.collectionField}/${structure?.id}/${message + businessPreference?.id
        }`
      );
    } else if (section === 'operations') {
      navigate(
        `/bos/element/${structure?.collectionField}/${structure?.id}/${message}`
      );
    }
    modalCloseHandler();
    setDone(false);
  };

  const listedValues = cleanedFields?.map((field) => field?.value);

  const displaySettings = () => {
    setIsSiteExtraOpen(true);
    setFieldSettings('SETTINGS');
  };


  return (
    <ModalLarge
      isOpen={isOpen}
      handlePrim={handleCreate}
      handleSec={handleClose}
      disabledPrim={disabledPrim}
      isCentered={done}
      subTitle={t('fillAndCreate')}
      displaySettings={displaySettings}
      modalCloseHandler={modalCloseHandler}
      icon={structure?.icon}
      title={t('create') + ' ' + (structure?.name || '')?.toLowerCase()}
      btnLabel={t('create')}
    >
      <div style={{ position: 'relative' }} className="d-flex">
        <div
          style={{
            position: 'relative',
            overflow: 'scroll',
            maxHeight: '62vh',
            height: '100%',
          }}
          className={
            structure?.element?.preferences?.sideExtra || isSiteExtraOpen
              ? 'col-7'
              : 'col-12'
          }
        >
          {!loading && !done && (
            <div>
              <div>
                <>
                  {fieldsOnCurrentPage?.map((field) => (
                    <FieldComponent
                      key={field?.value}
                      field={field}
                      value={formData[field?.value]}
                      values={listedValues}
                      fromCreation={true}
                      creationTargetId={creationTargetId}
                      error={errors[field.value]}
                      setCreationTargetId={setCreationTargetId}
                      onChange={handleFieldChange}
                      setCreationProfileId={setCreationProfileId}
                      parentElementCollection={parentElementCollection}
                      parentElementPath={parentElementPath}
                      setParentElementPath={setParentElementPath}
                      setParentElementCollection={setParentElementCollection}
                      parentData={parentData}
                      setParentData={setParentData}
                      formData={formData}
                      help={field?.help}
                      currentHoveredValue={currentHoveredValue}
                      currentValue={fieldSettings}
                      setCurrentHoveredValue={setCurrentHoveredValue}
                    // handleSettingsField={handleSettingsField}
                    />
                  ))}
                </>
              </div>
              {pages?.length > 1 && (
                <div className="d-flex align-c middle-content">
                  <Button
                    onClick={handlePrevPage}
                    label={t('previous')}
                    variant="text"
                    disabled={currentPage === 0}
                    buttonSx={{ mt: 1, mx: 2 }}
                  />
                  <Typography sx={{ mt: 0.6 }}>
                    Page {currentPage + 1} {t('of')} {pages?.length}
                  </Typography>
                  <Button
                    onClick={handleNextPage}
                    label={t('next')}
                    variant="text"
                    buttonSx={{ mt: 1, mx: 2 }}
                    disabled={currentPage === pages?.length - 1}
                  />
                </div>
              )}
            </div>
          )}
          {!loading && message && !done && (
            <div>
              {' '}
              <Typography
                variant="body1"
                color="error"
                fontSize="14px"
                fontWeight={400}
                textAlign="center"
              >
                {message}
              </Typography>
            </div>
          )}

          {loading && <Loading type="pulse" size="small" />}
          {!loading && done && (
            <div>
              <div className="align-c">
                {isAnimationPlaying && (
                  <Lottie
                    options={defaultOptions}
                    height={120}
                    width={250}
                    isStopped={!isAnimationPlaying}
                    isPaused={!isAnimationPlaying}
                  />
                )}
              </div>
              <div className="mb-5 mt-2">
                <Typography
                  variant="body1"
                  fontSize="14px"
                  fontWeight={400}
                  textAlign="center"
                >
                  {t('elementCreated')}
                </Typography>
              </div>
              {message && (
                <div className="d-flex align-c mb-4">
                  <Button
                    onClick={handleOpenElement}
                    label={t('open')}
                    fullWidth
                    buttonSx={{ marginRight: 2, marginLeft: 2 }}
                  />
                  <Button
                    onClick={handleClose}
                    label={t('close')}
                    fullWidth
                    variant="outlined"
                    buttonSx={{ marginRight: 2, marginLeft: 2 }}
                  />
                </div>
              )}
            </div>
          )}
        </div>
        {(structure?.element?.preferences?.sideExtra || isSiteExtraOpen) && (
          <div style={{ position: 'sticky', top: '0px' }} className="col-5">
            <SideExtra
              fieldSettings={fieldSettings}
              structure={structure}
              data={formData}
              secColor={businessPreference?.secColor}
              color={businessPreference?.mainColor}
              fields={fields}
              setFields={setFields}
              setFieldSettings={setFieldSettings}
              setIsSiteExtraOpen={setIsSiteExtraOpen}
              handleSettingsField={handleSettingsField}
            />
          </div>
        )}
      </div>
    </ModalLarge>
  );
};
export default ModalElementCreation;
